/**
 * Octopus IoT Platform - Routes Page
 * Fase B da integração LAB → principal: gestão de rotas (polilinha por porto).
 *
 * As rotas viram entidade gerenciável (coleção lab_routes via /api/lab/routes) e
 * passam a ser vinculáveis a geofences do tipo "Porto de descarregamento".
 *
 * IMPORTANTE: pontos são armazenados como [lon, lat] (GeoJSON); o Leaflet usa
 * [lat, lon]. A entrada do usuário é "latitude, longitude" por linha (igual ao
 * diálogo de polígono em GeofencesPage), convertida para [lon, lat].
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Waypoints, Plus, Pencil, Trash2, Loader2, RefreshCw, Search, Download, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { routesApi } from '@/services/routesApi';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Converte os pontos GeoJSON [lon, lat] para o formato do Leaflet [lat, lon].
const toLatLng = (pontos = []) =>
  pontos
    .filter((p) => Array.isArray(p) && p.length >= 2)
    .map(([lon, lat]) => [lat, lon]);

// Texto "lat, lon" por linha → pontos GeoJSON [lon, lat]. Retorna null se inválido.
const parseCoordinateText = (text) => {
  const pontos = (text || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[,\s;]+/).filter(Boolean).map(Number);
      if (parts.length < 2 || parts.some((value) => !Number.isFinite(value))) return null;
      const [lat, lon] = parts;
      if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
      return [lon, lat];
    });
  if (pontos.length < 2 || pontos.some((p) => !p)) return null;
  return pontos;
};

const pontosToText = (pontos = []) =>
  pontos
    .filter((p) => Array.isArray(p) && p.length >= 2)
    .map(([lon, lat]) => `${lat}, ${lon}`)
    .join('\n');

const FitRouteBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    map.fitBounds(positions, { padding: [24, 24] });
  }, [positions, map]);
  return null;
};

const RoutesPage = () => {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formId, setFormId] = useState('');
  const [formDestino, setFormDestino] = useState('');
  const [formOrigem, setFormOrigem] = useState('');
  const [formCoords, setFormCoords] = useState('');
  const [replaceParsing, setReplaceParsing] = useState(false);
  const [replaceCandidates, setReplaceCandidates] = useState([]);
  const [replaceFileName, setReplaceFileName] = useState('');
  const replaceFileInputRef = useRef(null);

  useEffect(() => {
    loadRoutes();
  }, []);

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const response = await routesApi.listRoutes();
      const list = (response.data?.routes || []).slice().sort((a, b) =>
        String(a.id).localeCompare(String(b.id), 'pt-BR')
      );
      setRoutes(list);
    } catch (error) {
      console.error('Error loading routes:', error);
      toast.error('Erro ao carregar rotas');
    } finally {
      setLoading(false);
    }
  };

  const filteredRoutes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return routes.filter((r) =>
      String(r.id).toLowerCase().includes(term) ||
      String(r.destino || '').toLowerCase().includes(term) ||
      String(r.origem || '').toLowerCase().includes(term)
    );
  }, [routes, searchTerm]);

  const selectedRoute = useMemo(
    () => routes.find((r) => r.id === selectedId) || null,
    [routes, selectedId]
  );

  const openCreate = () => {
    setEditingRoute(null);
    setFormId('');
    setFormDestino('');
    setFormOrigem('');
    setFormCoords('');
    setDialogOpen(true);
  };

  const openEdit = (route) => {
    setEditingRoute(route);
    setFormId(route.id);
    setFormDestino(route.destino || '');
    setFormOrigem(route.origem || '');
    setFormCoords(pontosToText(route.pontos));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const id = formId.trim().toUpperCase();
    if (!editingRoute && !id) {
      toast.error('Informe o identificador da rota (ex.: MCR)');
      return;
    }
    const pontos = parseCoordinateText(formCoords);
    if (!pontos) {
      toast.error('Informe ao menos 2 linhas no formato "latitude, longitude"');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        destino: formDestino.trim() || null,
        origem: formOrigem.trim() || null,
        pontos,
      };
      if (editingRoute) {
        await routesApi.updateRoute(editingRoute.id, payload);
        toast.success(`Rota "${editingRoute.id}" atualizada`);
      } else {
        await routesApi.createRoute({ id, ...payload });
        toast.success(`Rota "${id}" criada`);
        setSelectedId(id);
      }
      setDialogOpen(false);
      await loadRoutes();
    } catch (error) {
      console.error('Error saving route:', error);
      const detail = error.response?.data?.detail || 'Erro ao salvar rota';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  // Baixa o KMZ da rota (mesmo conteúdo que pode ser re-importado depois).
  const handleDownloadKmz = async (route) => {
    try {
      const res = await routesApi.exportRoute(route.id);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: 'application/vnd.google-earth.kmz' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `rota_${route.id}.kmz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading route KMZ:', error);
      toast.error('Erro ao baixar o KMZ da rota');
    }
  };

  // Substitui as coordenadas do formulário pelos pontos de uma LineString do arquivo.
  // Persiste só ao "Salvar" (PUT mantém o mesmo id → preserva os vínculos).
  const applyImportedLine = (line) => {
    const pontos = line?.pontos || [];
    if (pontos.length < 2) {
      toast.error('Linha inválida no arquivo (mínimo de 2 pontos)');
      return;
    }
    setFormCoords(pontosToText(pontos));
    toast.success('Coordenadas substituídas pelo arquivo. Revise no mapa e clique em Salvar.');
  };

  const handleSelectReplaceFile = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.kmz', '.kml'].includes(ext)) {
      toast.error('Arquivo inválido', { description: 'Selecione um arquivo .kmz ou .kml' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande', { description: 'O arquivo deve ter no máximo 10MB' });
      return;
    }
    setReplaceParsing(true);
    try {
      const res = await routesApi.parseFile(file);
      const lines = res.data?.lines || [];
      if (!lines.length) {
        toast.error('Nenhuma linha encontrada', {
          description: 'O arquivo não contém LineString para substituir esta rota.',
        });
        return;
      }
      if (lines.length === 1) {
        applyImportedLine(lines[0]);
      } else {
        setReplaceFileName(file.name);
        setReplaceCandidates(lines);
      }
    } catch (error) {
      console.error('Error parsing route file:', error);
      const detail = error.response?.data?.detail || 'Erro ao processar arquivo';
      toast.error('Erro ao ler arquivo', { description: detail });
    } finally {
      setReplaceParsing(false);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await routesApi.deleteRoute(deleteConfirm.id);
      setRoutes((prev) => prev.filter((r) => r.id !== deleteConfirm.id));
      if (selectedId === deleteConfirm.id) setSelectedId(null);
      toast.success(`Rota "${deleteConfirm.id}" excluída`);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error('Erro ao excluir rota');
    }
  };

  const selectedPositions = toLatLng(selectedRoute?.pontos);

  return (
    <div className="page-container-fill gap-6" data-testid="routes-page">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Waypoints className="h-6 w-6 text-primary" />
            Rotas
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Rotas de navegação (polilinha por porto), vinculáveis às geofences de descarregamento
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-route-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nova Rota
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-4 shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar rotas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="route-search"
          />
        </div>
        <Button variant="outline" onClick={loadRoutes}>
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 grid-rows-1 gap-6 flex-1 min-h-0">
        {/* List */}
        <Card className="h-full min-h-[360px] flex flex-col min-w-0">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Lista de Rotas ({filteredRoutes.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredRoutes.length === 0 ? (
              <div className="text-center py-8">
                <Waypoints className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {searchTerm ? 'Nenhuma rota encontrada' : 'Nenhuma rota cadastrada'}
                </p>
              </div>
            ) : (
              <ScrollArea className="flex-1 min-h-0">
                <div className="space-y-2 pr-4">
                  {filteredRoutes.map((route) => (
                    <div
                      key={route.id}
                      className={cn(
                        'p-3 border rounded-lg cursor-pointer transition-all',
                        selectedId === route.id ? 'border-primary bg-primary/5' : 'hover:border-border/80'
                      )}
                      onClick={() => setSelectedId(route.id)}
                      data-testid={`route-item-${route.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{route.id}</Badge>
                            <p className="font-medium text-sm truncate">{route.destino || 'Sem destino'}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {route.origem ? `Origem: ${route.origem} • ` : ''}
                            {Number.isFinite(Number(route.comprimento_km))
                              ? `${Number(route.comprimento_km).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`
                              : '— km'}
                            {' • '}
                            {(route.pontos?.length ?? 0)} pontos
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); handleDownloadKmz(route); }}
                            data-testid={`route-download-${route.id}`}
                            title="Baixar KMZ"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); openEdit(route); }}
                            data-testid={`route-edit-${route.id}`}
                            title="Editar rota"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:bg-destructive/10"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(route); }}
                            data-testid={`route-delete-${route.id}`}
                            title="Excluir rota"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="h-full min-h-[360px] flex flex-col min-w-0">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm">
              {selectedRoute ? `Rota ${selectedRoute.id} — ${selectedRoute.destino || ''}` : 'Mapa'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <div className="rounded-lg overflow-hidden border border-border flex-1 min-h-0" style={{ minHeight: '260px' }}>
              <MapContainer center={[-24.3, -54.3]} zoom={9} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                />
                {selectedPositions.length >= 2 && (
                  <>
                    <FitRouteBounds positions={selectedPositions} />
                    <Polyline positions={selectedPositions} pathOptions={{ color: '#38BDF8', weight: 4 }} />
                    <CircleMarker center={selectedPositions[0]} radius={6} pathOptions={{ color: '#22C55E', fillColor: '#22C55E', fillOpacity: 1 }} />
                    <CircleMarker center={selectedPositions[selectedPositions.length - 1]} radius={6} pathOptions={{ color: '#EF4444', fillColor: '#EF4444', fillOpacity: 1 }} />
                  </>
                )}
              </MapContainer>
            </div>
            {selectedRoute && (
              <p className="text-xs text-muted-foreground mt-2">
                Ponto verde = origem (índice {selectedRoute.origin_index ?? 0}); vermelho = destino.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col">
          <DialogHeader>
            <DialogTitle>{editingRoute ? `Editar rota ${editingRoute.id}` : 'Nova rota'}</DialogTitle>
            <DialogDescription>
              Informe uma coordenada por linha no formato "latitude, longitude". O comprimento é calculado no servidor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Identificador</Label>
                <Input
                  value={formId}
                  onChange={(e) => setFormId(e.target.value.toUpperCase())}
                  placeholder="Ex.: MCR"
                  disabled={!!editingRoute}
                  data-testid="route-form-id"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Destino</Label>
                <Input
                  value={formDestino}
                  onChange={(e) => setFormDestino(e.target.value)}
                  placeholder="Ex.: Marechal Cândido Rondon"
                  data-testid="route-form-destino"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Origem</Label>
              <Input
                value={formOrigem}
                onChange={(e) => setFormOrigem(e.target.value)}
                placeholder="Ex.: Guaíra"
                data-testid="route-form-origem"
              />
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Coordenadas (latitude, longitude por linha)</Label>
                <div className="flex flex-wrap gap-2">
                  {editingRoute && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadKmz(editingRoute)}
                      data-testid="route-download-edit"
                      title="Baixar o KMZ atual desta rota"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Baixar KMZ
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => replaceFileInputRef.current?.click()}
                    disabled={replaceParsing}
                    data-testid="route-replace-file"
                    title="Substituir os pontos a partir de um arquivo KMZ/KML"
                  >
                    {replaceParsing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileUp className="h-4 w-4 mr-2" />
                    )}
                    Substituir por KMZ
                  </Button>
                  <input
                    ref={replaceFileInputRef}
                    type="file"
                    accept=".kmz,.kml"
                    className="hidden"
                    onChange={(e) => handleSelectReplaceFile(e.target.files)}
                  />
                </div>
              </div>
              <Textarea
                value={formCoords}
                onChange={(e) => setFormCoords(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                placeholder={'-24.0838, -54.2967\n-24.0935, -54.3041\n-24.0965, -54.3070'}
                data-testid="route-form-coords"
              />
              <p className="text-xs text-muted-foreground">
                A primeira linha é a origem (ponto verde); a última é o destino. Mínimo de 2 pontos.
                {editingRoute && ' Substituir mantém o mesmo id da rota — as geofences vinculadas não se perdem.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRoute ? 'Salvar' : 'Criar Rota'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escolher linha do arquivo (quando há mais de uma LineString) */}
      <Dialog open={replaceCandidates.length > 0} onOpenChange={(open) => !open && setReplaceCandidates([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolher linha do arquivo</DialogTitle>
            <DialogDescription>
              O arquivo "{replaceFileName}" contém {replaceCandidates.length} linhas. Escolha qual substituirá os pontos desta rota.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[320px]">
            <div className="space-y-2 pr-3">
              {replaceCandidates.map((line, index) => (
                <button
                  key={`replace-line-${index}`}
                  type="button"
                  className="w-full text-left p-3 border rounded-md transition-all hover:border-primary hover:bg-primary/5"
                  onClick={() => { applyImportedLine(line); setReplaceCandidates([]); }}
                  data-testid={`route-replace-candidate-${index}`}
                >
                  <p className="font-medium text-sm truncate">{line.name || `Linha #${index + 1}`}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {line.point_count} pontos • {Number(line.length_km).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km
                  </p>
                </button>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceCandidates([])}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir rota?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso excluirá a rota "{deleteConfirm?.id}". Geofences de porto vinculadas a ela ficarão sem rota.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoutesPage;
